import { useSnackbar } from 'notistack';
import { useState, useEffect, useRef } from 'react';
import { User, Camera, Loader2 } from 'lucide-react';

import actionEditarPerfil from 'src/actions/usuarios/actionEditarPerfil';
import actionEditarSenha from 'src/actions/usuarios/actionEditarSenhaUsuario';
import actionDetalhesUsuario from 'src/actions/usuarios/actionDetalhesUsuario';
import actionUploadAvatar from 'src/actions/usuarios/actionUploadAvatar';

import PerfilAlterarSenha from 'src/components/_dashboard/perfil/PerfilAlterarSenha';
import PerfilGeral from 'src/components/_dashboard/perfil/PerfilGeral';
import useAuth from 'src/hooks/useAuth';
import { useSomNotificacao } from 'src/hooks/useNotificacoes';
import Preferencias from 'src/components/_dashboard/perfil/Preferencias';
import { soNumeros } from 'src/utils/functions';
import v2_actionEditarUsuario from 'src/actions/v2/usuarios/v2_actionEditarUsuario';
import { PERFIL_COMPONENT_DESIGN } from './PerfilComponent.design';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/@/components/ui/tabs"

export default function PerfilComponent() {
  const { enqueueSnackbar } = useSnackbar();
  const [nome, setNome] = useState('');
  const [sobrenome, setSobrenome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [usuario, setUsuario] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const { ligado: somLigado, alternar: alternarSom } = useSomNotificacao();
  const { updateProfile, user, cliente } = useAuth();
  const [mensagemIntervalo, setMensagemIntervalo] = useState(null);
  const [isModificado, setIsModificado] = useState(false);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    buscarDetalhesUsuario()
  }, []);

  const handleEditarSenha = () => {
    setIsLoading(true)
    if (password === repeatPassword) {
      actionEditarSenha({ usuarioId: user.id, password: password, repeatPassword: repeatPassword }).then(() => {
        enqueueSnackbar('Senha alterada com sucesso', { variant: 'success' });
      }).catch(() => {
        enqueueSnackbar('Erro ao alterar senha', { variant: 'error' });
      })
    } else {
      enqueueSnackbar('Senhas não conferem', { variant: 'error' });
    }
  }

  function maskUsername(str) {
    return str.split(' ').join('')
  }

  const handleEditarPerfil = () => {
    setIsLoading(true);

    let data = {
      usuarioId: user.id,
      firstName: nome,
      lastName: sobrenome,
      email,
      telefone: `55${soNumeros(telefone)}`,
      username: maskUsername(usuario),
      nomeFantasia
    }

    actionEditarPerfil(data).then(res => {
      if (res.data.error === true) {
        enqueueSnackbar(res.data.mensagem, { variant: 'error' });
      } else {
        updateProfile({
          firstName: nome,
          lastName: sobrenome,
          email,
          username: maskUsername(usuario),
          nomeFantasia
        });
        enqueueSnackbar(res.data.mensagem, { variant: 'success' });
      }
    }).catch(() => {
      enqueueSnackbar('Erro ao alterar perfil', { variant: 'error' });
    }).finally(() => {
      setIsLoading(false);
    })
  }

  // Ver comentario em pages/dashboard/Perfil: o toggle antigo era no-op quando a
  // chave nao existia e recarregava a pagina quando existia.
  const changeSoundNotification = () => alternarSom();

  const handleUploadAvatar = async (file) => {
    setAvatarLoading(true);
    try {
      const res = await actionUploadAvatar(user.id, file);
      if (!res.data.error) {
        setAvatar(res.data.data.avatar);
        updateProfile({ avatar: res.data.data.avatar });
        enqueueSnackbar('Foto atualizada com sucesso', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Erro ao atualizar foto', { variant: 'error' });
    } finally {
      setAvatarLoading(false);
    }
  }

  async function handelEditarPreferencias() {
    setIsLoading(true);
    try {
      let data = {
        "scope": {
          "config.messageStandby": mensagemIntervalo,
          "config.betaConfig": config.betaConfig
        }
      }
      await v2_actionEditarUsuario(user.id, data);
      enqueueSnackbar('Alterado com sucesso', { variant: 'success' });
    } catch (erro) {
      setIsLoading(false);
      setTimeout(() => {
        enqueueSnackbar('Erro ao alterar perfil', { variant: 'error' });
      }, 1000);
    }
  }

  const buscarDetalhesUsuario = async () => {
    try {
      const res = await actionDetalhesUsuario(user.id);
      if (res.data.data) {
        let dataUsuario = res.data.data;
        const rawPhone = dataUsuario.telefone;
        setTelefone(rawPhone ? rawPhone.substring(2) : '');
        setAvatar(dataUsuario.avatar || null);
        setNome(dataUsuario.firstName);
        setSobrenome(dataUsuario.lastName);
        setEmail(dataUsuario.email);
        setUsuario(dataUsuario.username);
        setNomeFantasia(dataUsuario.nomeFantasia);
        setMensagemIntervalo(dataUsuario?.config?.messageStandby);
        setConfig(dataUsuario.config);
      }
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <div className={PERFIL_COMPONENT_DESIGN.root} style={{ fontFamily: PERFIL_COMPONENT_DESIGN.fontStack }}>
      <Tabs defaultValue="general" className="w-full">
        <div className={PERFIL_COMPONENT_DESIGN.stickyHeader}>
          <div className={PERFIL_COMPONENT_DESIGN.container}>
            <div className={PERFIL_COMPONENT_DESIGN.topbar}>
              <div className={PERFIL_COMPONENT_DESIGN.topbarIdentity}>
                <div className="min-w-0">
                  <h1 className={PERFIL_COMPONENT_DESIGN.title}>Conta</h1>
                  <p className={PERFIL_COMPONENT_DESIGN.subtitle}>
                    Dados pessoais, senha e preferências.
                  </p>
                </div>
              </div>
            </div>
            <TabsList className={PERFIL_COMPONENT_DESIGN.tabsList}>
              <TabsTrigger value="general" className={PERFIL_COMPONENT_DESIGN.tab}>Perfil</TabsTrigger>
              <TabsTrigger value="change_password" className={PERFIL_COMPONENT_DESIGN.tab}>Senha</TabsTrigger>
              <TabsTrigger value="preferencias" className={PERFIL_COMPONENT_DESIGN.tab}>Preferências</TabsTrigger>
            </TabsList>
          </div>
        </div>

        <div className={`${PERFIL_COMPONENT_DESIGN.container} pt-6 ${PERFIL_COMPONENT_DESIGN.pageBody}`}>
        <TabsContent value="general" className={PERFIL_COMPONENT_DESIGN.stack}>
          <div className={PERFIL_COMPONENT_DESIGN.sectionHeader}>
            <h2 className={PERFIL_COMPONENT_DESIGN.sectionHeaderTitle}>Dados pessoais</h2>
            <p className={PERFIL_COMPONENT_DESIGN.sectionHeaderSub}>Como você aparece para a equipe.</p>
          </div>
          <div className={PERFIL_COMPONENT_DESIGN.card}>
            {/* Avatar */}
            <div className={PERFIL_COMPONENT_DESIGN.avatar.container}>
              <div className={PERFIL_COMPONENT_DESIGN.avatar.wrapper}>
                <div
                  className={PERFIL_COMPONENT_DESIGN.avatar.imageContainer}
                  onClick={() => !avatarLoading && fileInputRef.current?.click()}
                >
                  {avatar ? (
                    <img src={avatar} alt="Avatar" className={PERFIL_COMPONENT_DESIGN.avatar.image} />
                  ) : (
                    <User size={32} className={PERFIL_COMPONENT_DESIGN.avatar.placeholder} />
                  )}
                  <div className={PERFIL_COMPONENT_DESIGN.avatar.overlay}>
                    {avatarLoading ? (
                      <Loader2 size={20} className="text-white animate-spin" />
                    ) : (
                      <Camera size={20} className="text-white" />
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadAvatar(file);
                    e.target.value = '';
                  }}
                />
                <div>
                  <p className={PERFIL_COMPONENT_DESIGN.avatar.name}>{nome} {sobrenome}</p>
                  <p className={PERFIL_COMPONENT_DESIGN.avatar.email}>{email}</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={PERFIL_COMPONENT_DESIGN.avatar.changeButton}
                    disabled={avatarLoading}
                  >
                    Alterar foto
                  </button>
                </div>
              </div>
            </div>

            <div className={PERFIL_COMPONENT_DESIGN.divider} />

            <PerfilGeral
              nome={nome}
              sobrenome={sobrenome}
              email={email}
              telefone={telefone}
              usuario={maskUsername(usuario)}
              nomeFantasia={nomeFantasia}
              changeTelefone={e => setTelefone(e.target.value)}
              changeNome={e => setNome(e.target.value)}
              changeSobrenome={e => setSobrenome(e.target.value)}
              changeEmail={e => setEmail(e.target.value)}
              changeUsuario={e => setUsuario(e.target.value)}
              changeNomeFantasia={e => setNomeFantasia(e.target.value)}
              submit={handleEditarPerfil}
              isLoading={isLoading}
            />
          </div>
        </TabsContent>

        {/* Senha */}
        <TabsContent value="change_password" className={PERFIL_COMPONENT_DESIGN.stack}>
          <div className={PERFIL_COMPONENT_DESIGN.sectionHeader}>
            <h2 className={PERFIL_COMPONENT_DESIGN.sectionHeaderTitle}>Senha</h2>
            <p className={PERFIL_COMPONENT_DESIGN.sectionHeaderSub}>Defina uma nova senha de acesso.</p>
          </div>
          <div className={PERFIL_COMPONENT_DESIGN.card}>
            <PerfilAlterarSenha
              password={password}
              repeatPassword={repeatPassword}
              changePassword={e => setPassword(e.target.value)}
              changeRepeatPassword={e => setRepeatPassword(e.target.value)}
              isLoading={isLoading}
              submit={handleEditarSenha}
            />
          </div>
        </TabsContent>

        {/* Preferências */}
        <TabsContent value="preferencias" className={PERFIL_COMPONENT_DESIGN.stack}>
          <div className={PERFIL_COMPONENT_DESIGN.sectionHeader}>
            <h2 className={PERFIL_COMPONENT_DESIGN.sectionHeaderTitle}>Preferências</h2>
            <p className={PERFIL_COMPONENT_DESIGN.sectionHeaderSub}>Notificações e aparência da sua conta.</p>
          </div>
          <div className={PERFIL_COMPONENT_DESIGN.card}>
            <Preferencias
              user={user}
              cliente={cliente}
              mensagemIntervalo={mensagemIntervalo}
              changeMensagemIntervalo={e => setMensagemIntervalo(e.target.value)}
              isModificado={isModificado}
              changeIsModificado={() => setIsModificado(true)}
              checkedSomNotificacao={somLigado}
              changeSomNotificacao={changeSoundNotification}
              onSubmit={handelEditarPreferencias}
              config={config}
              changeConfig={e => {
                const { name, value } = e;
                setIsModificado(true);
                setConfig((prevConfig) => ({
                  ...prevConfig,
                  [name]: {
                    ...prevConfig[name],
                    ...value,
                  },
                }));
              }}
            />
          </div>
        </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
